import { Anchor, Group, Loader, ScrollArea, Stack } from '@mantine/core';
import type { TeamOut } from '@repo/api-codegen';
import { useApiListTeamsV2 } from '@repo/api-codegen/v2';
import { Button, Text, Title } from '@repo/foundations';
import { useCallback, useRef } from 'react';
import { AuthCard } from '../../../components/Auth/AuthCard';
import { AuthFooter } from '../../../components/Auth/AuthLayouts/AuthFooter';
import type { SyncStepActions } from '../../../components/Integration/IntegrationSelectionPage/StepComponents';
import {
	MappingStep,
	SelectStep,
	SyncStep,
} from '../../../components/Integration/IntegrationSelectionPage/StepComponents';
import { ConnectionStep } from '../../../components/Integration/IntegrationSelectionPage/StepComponents/ConnectionStep';
import useCreateIntegrationConnection, {
	IntegrationSteps,
} from '../../../components/Integration/IntegrationSelectionPage/useCreateIntegrationConnection';
import { OnboardingStepper } from './OnboardingStepper';

export function SecodaLiteIntegrationSetupStep({
	onBack,
	onNext,
}: {
	onBack: () => void;
	onNext: () => void;
}) {
	const { data: teams, isLoading: isLoadingTeams } = useApiListTeamsV2({});

	const {
		integrationSpec,
		createdIntegration,
		setCreatedIntegration,
		currentStep,
		currentStepName,
		prevStep,
		nextStep,
		handleClose,
		stepNames,
	} = useCreateIntegrationConnection();

	const connectionStepActionsRef = useRef<{
		onSubmit: () => void;
		canSubmit: () => boolean;
	}>(null);
	const syncStepActionsRef = useRef<SyncStepActions>(null);

	const handleBack = useCallback(() => {
		if (currentStepName === IntegrationSteps.CONNECTION) {
			handleClose(() => {
				onBack();
			});
		} else {
			prevStep();
		}
	}, [currentStepName, handleClose, onBack, prevStep]);

	const handleSubmit = useCallback(() => {
		if (currentStepName === IntegrationSteps.CONNECTION) {
			connectionStepActionsRef.current?.onSubmit();
		} else if (currentStepName === IntegrationSteps.SYNC) {
			syncStepActionsRef.current?.submit();
		} else {
			nextStep();
		}
	}, [currentStepName, nextStep]);

	if (integrationSpec.type !== 'builtin') {
		return null;
	}

	return (
		<AuthCard width={800} p="2xl">
			<Stack spacing="xl">
				{currentStepName === IntegrationSteps.CONNECTION && (
					<Stack spacing={0}>
						<Title>Connect {integrationSpec.value.name}</Title>
						<Text color="text/secondary/default">
							<Anchor
								href={integrationSpec.value.helpArticle}
								target="_blank"
								rel="noreferrer"
							>
								View documentation
							</Anchor>
						</Text>
					</Stack>
				)}
				{currentStepName === IntegrationSteps.IMPORT && (
					<Stack spacing={0}>
						<Title>Import {integrationSpec.value.name}</Title>
						<Text color="text/secondary/default">
							Select the schemas you'd like to include in the sync.{' '}
							<Anchor
								href={integrationSpec.value.helpArticle}
								target="_blank"
								rel="noreferrer"
							>
								View documentation
							</Anchor>
						</Text>
					</Stack>
				)}
				<ScrollArea.Autosize mah={428}>
					{currentStepName === IntegrationSteps.CONNECTION &&
						(isLoadingTeams ? (
							<Loader />
						) : (
							<ConnectionStep
								key={integrationSpec.value.type}
								integrationSpec={integrationSpec}
								integration={createdIntegration}
								setIntegration={setCreatedIntegration}
								nextStep={nextStep}
								hideTitle
								hideSubmit
								hideDetailsSection
								initialTeams={
									teams
										?.filter((item: TeamOut) => item.is_default_team)
										.map((item: TeamOut) => item.id) || []
								}
								actionsRef={connectionStepActionsRef}
							/>
						))}
					{currentStepName === IntegrationSteps.IMPORT &&
						createdIntegration && (
							<SelectStep
								integration={createdIntegration}
								integrationSpec={integrationSpec}
								nextStep={nextStep}
								hideTitle
								hideSubmit
								maxHeight="auto"
								height="347px"
							/>
						)}
					{currentStepName === IntegrationSteps.MAPPING &&
						createdIntegration && (
							<MappingStep
								integration={createdIntegration}
								nextStep={nextStep}
							/>
						)}
					{currentStepName === IntegrationSteps.SYNC && createdIntegration && (
						<SyncStep
							integrationSpec={integrationSpec}
							integration={createdIntegration}
							hideTitle
							hideSubmit
							actionsRef={syncStepActionsRef}
							onDone={onNext}
						/>
					)}
				</ScrollArea.Autosize>
			</Stack>
			<AuthFooter>
				<Group position="apart" py="md" px="2xl">
					<OnboardingStepper
						step={2 + currentStep}
						totalSteps={2 + stepNames.length}
					/>
					<Group spacing="xs" position="right">
						<Button variant="default" onClick={handleBack}>
							Back
						</Button>
						<Button onClick={handleSubmit} variant="primary">
							Continue
						</Button>
					</Group>
				</Group>
			</AuthFooter>
		</AuthCard>
	);
}
